import { FilterXSS, type IFilterXSSOptions } from "xss"

/**
 * Sanitize user- or API-controlled HTML before using dangerouslySetInnerHTML.
 * Strips unknown tags and script/style bodies; js-xss applies a safe default whitelist.
 * When you only need plain text, prefer React children so content is escaped by default.
 */
const OPTIONS: IFilterXSSOptions = {
  stripIgnoreTag: true,
  stripIgnoreTagBody: ["script", "style"],
}

const filter = new FilterXSS(OPTIONS)

function toDisplayableHref(rawHref: string): string | null {
  const href = rawHref.trim()
  if (!href) return null

  if (href.startsWith("/")) {
    return href
  }

  try {
    const url = new URL(href)
    if (
      url.protocol === "http:" ||
      url.protocol === "https:" ||
      url.protocol === "mailto:" ||
      url.protocol === "tel:"
    ) {
      return url.toString()
    }
    return null
  } catch {
    return null
  }
}

function replaceAnchorTagsWithText(input: string): string {
  return input.replace(
    /<a\b[^>]*href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a>/gi,
    (_match, _hrefAttr, dblQuoted, singleQuoted, unquoted, innerText) => {
      const rawHref = dblQuoted ?? singleQuoted ?? unquoted ?? ""
      const safeHref = toDisplayableHref(rawHref)
      if (!safeHref) return innerText
      return `${innerText} (${safeHref})`
    },
  )
}

export function sanitizeUntrustedHtml(
  input: string | null | undefined,
): string {
  if (input == null || input === "") return ""
  const normalizedInput = replaceAnchorTagsWithText(String(input))
  return filter.process(normalizedInput)
}
